import {
    Add,
    Block,
    ContentCopy,
    Delete,
    Edit,
    PlayCircleOutline,
    Refresh,
} from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "app/providers/AuthProvider";
import {
    ConfirmActionDialog,
    EmptyState,
    FilterBar,
    FlussPageHeader,
    FlussStatusChip,
} from "modules/fluss/components/design-kit/FlussDesignKit";
import { flussApi, flussApiError } from "modules/fluss/services/fluss-api";
import { FlussDefinition } from "modules/fluss/types/fluss";
import { createEmptyFlussXml, toDefinitionKey } from "modules/fluss/utils/bpmn";
import { flussDefinitionDescription, flussDefinitionLabel } from "modules/fluss/utils/flussDefinitionLabel";
import { canAutomateFluss, canDeleteFlussDefinition, canDesignFluss } from "modules/fluss/utils/permissions";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";

const FlussRepositoryPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { user } = useAuth();
    const canDesign = canDesignFluss(user);
    const canDeactivate = canAutomateFluss(user);
    const canDelete = canDeleteFlussDefinition(user);
    const [search, setSearch] = useState("");
    const [createOpen, setCreateOpen] = useState(false);
    const [name, setName] = useState("");
    const [definitionKey, setDefinitionKey] = useState("");
    const [keyTouched, setKeyTouched] = useState(false);
    const [description, setDescription] = useState("");
    const [copySource, setCopySource] = useState<FlussDefinition | null>(null);
    const [lifecycleTarget, setLifecycleTarget] = useState<
        { definition: FlussDefinition; action: "disable" | "enable" | "delete" } | null
    >(null);
    const [lifecycleError, setLifecycleError] = useState<string | null>(null);

    const definitionsQuery = useQuery({
        queryKey: ["fluss", "definitions"],
        queryFn: flussApi.listDefinitions,
        enabled: canDesign,
    });

    const createMutation = useMutation({
        mutationFn: async () => {
            const key = definitionKey || toDefinitionKey(name);
            const source = copySource ? await flussApi.getDefinition(copySource.id) : null;
            const sourceXml = source?.xml_draft;
            if (source && !sourceXml) {
                throw new Error(String(t("fluss.repository.copy_missing_source_xml", {
                    defaultValue: "The source definition has no saved diagram to copy.",
                })));
            }
            return flussApi.createDefinition({
                definition_key: key,
                name,
                description,
                xml_draft: sourceXml ?? createEmptyFlussXml(key, name),
            });
        },
        onSuccess: async (definition) => {
            await queryClient.invalidateQueries({ queryKey: ["fluss", "definitions"] });
            setCreateOpen(false);
            navigate(`/home/fluss/definitions/${definition.id}`);
        },
    });

    const lifecycleMutation = useMutation({
        mutationFn: async () => {
            if (!lifecycleTarget) return null;
            if (lifecycleTarget.action === "disable") return flussApi.disableDefinition(lifecycleTarget.definition.id);
            if (lifecycleTarget.action === "enable") return flussApi.enableDefinition(lifecycleTarget.definition.id);
            await flussApi.deleteDefinition(lifecycleTarget.definition.id);
            return null;
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ["fluss", "definitions"] });
            setLifecycleTarget(null);
            setLifecycleError(null);
        },
        onError: (error) => {
            setLifecycleError(flussApiError(error).message);
        },
    });

    const latestDefinitions = useMemo(() => {
        const latest = new Map<string, FlussDefinition>();
        (definitionsQuery.data || []).forEach((definition) => {
            const current = latest.get(definition.definition_key);
            if (!current || current.version < definition.version) latest.set(definition.definition_key, definition);
        });
        const normalizedSearch = search.trim().toLowerCase();
        return Array.from(latest.values())
            .map((definition) => ({ definition, label: flussDefinitionLabel(t, definition.name) }))
            .filter(({ definition, label }) =>
                !normalizedSearch ||
                label.toLowerCase().includes(normalizedSearch) ||
                definition.definition_key.toLowerCase().includes(normalizedSearch)
            );
    }, [definitionsQuery.data, search, t]);

    // Desativar/Reativar act on whichever version is currently is_active for the family, which is
    // NOT always the row shown above (a card shows the latest version by number — e.g. right after
    // "Editar / Nova versão" that's a fresh draft, while the active one is still the previous,
    // published version). Looked up separately so the button/badge reflect the real active state
    // instead of silently hiding once a newer draft exists.
    const activeDefinitionByKey = useMemo(() => {
        const active = new Map<string, FlussDefinition>();
        (definitionsQuery.data || []).forEach((definition) => {
            if (definition.is_active) active.set(definition.definition_key, definition);
        });
        return active;
    }, [definitionsQuery.data]);

    const openCreate = () => {
        createMutation.reset();
        setCopySource(null);
        setName("");
        setDefinitionKey("");
        setKeyTouched(false);
        setDescription("");
        setCreateOpen(true);
    };

    const openCopy = (definition: FlussDefinition) => {
        createMutation.reset();
        const sourceName = flussDefinitionLabel(t, definition.name);
        const copyName = t("fluss.repository.copy_name_suffix", {
            defaultValue: "{{name}} (copy)",
            name: sourceName,
        });
        setCopySource(definition);
        setName(copyName);
        setDefinitionKey(toDefinitionKey(copyName));
        setKeyTouched(false);
        setDescription(flussDefinitionDescription(t, definition.description));
        setCreateOpen(true);
    };

    if (!canDesign) return <Navigate to="/home/fluss/requests" replace />;

    if (definitionsQuery.isLoading) {
        return <Box className="fluss-page" display="grid" sx={{ placeItems: "center" }}><CircularProgress /></Box>;
    }

    return (
        <Box className="fluss-page">
            <Stack gap={3}>
                <FlussPageHeader
                    title={t("fluss.repository.title", { defaultValue: "Fluss definitions" })}
                    subtitle={t("fluss.repository.subtitle", { defaultValue: "Design, validate and publish digital workflows." })}
                    actions={(
                        <>
                            <Tooltip title={t("fluss.common.refresh", { defaultValue: "Refresh" })}>
                                <IconButton onClick={() => definitionsQuery.refetch()}><Refresh /></IconButton>
                            </Tooltip>
                            <Button startIcon={<Add />} onClick={openCreate}>
                                {t("fluss.repository.create", { defaultValue: "Create definition" })}
                            </Button>
                        </>
                    )}
                />

                {definitionsQuery.error && (
                    <Alert severity="error">{flussApiError(definitionsQuery.error).message}</Alert>
                )}

                <FilterBar search={search} onSearch={setSearch} />

                {latestDefinitions.length === 0 ? (
                    <EmptyState
                        title={t("fluss.repository.empty_title", { defaultValue: "No workflow definitions yet" })}
                        description={t("fluss.repository.empty_description", {
                            defaultValue: "Create the first definition to start drawing a process.",
                        })}
                        actionLabel={t("fluss.repository.create", { defaultValue: "Create definition" })}
                        onAction={openCreate}
                    />
                ) : (
                    <Stack gap={1.5}>
                        {latestDefinitions.map(({ definition, label }) => {
                            const activeDefinition = activeDefinitionByKey.get(definition.definition_key);
                            const visibleDescription = flussDefinitionDescription(t, definition.description);
                            return (
                            <Paper key={definition.definition_key} variant="outlined" sx={{ p: 2 }}>
                                <Stack direction={{ xs: "column", sm: "row" }} gap={2} alignItems={{ sm: "center" }}>
                                    <Box flex={1} minWidth={0}>
                                        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                                            <Typography variant="h6">{label}</Typography>
                                            <FlussStatusChip status={definition.status} />
                                            {activeDefinition?.disabled && (
                                                <Chip
                                                    size="small"
                                                    color="warning"
                                                    label={t("fluss.repository.disabled_badge", { defaultValue: "Deactivated" })}
                                                />
                                            )}
                                        </Stack>
                                        <Typography variant="body2" color="text.secondary">
                                            {t("fluss.repository.key_version", {
                                                key: definition.definition_key,
                                                version: definition.version,
                                                defaultValue: `${definition.definition_key} · v${definition.version}`,
                                            })}
                                        </Typography>
                                        {visibleDescription && (
                                            <Typography variant="body2" color="text.secondary" noWrap>
                                                {visibleDescription}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Stack direction="row" gap={1} flexWrap="wrap">
                                        {canDeactivate && activeDefinition && (
                                            activeDefinition.disabled ? (
                                                <Tooltip title={t("fluss.repository.enable", { defaultValue: "Activate" })}>
                                                    <IconButton
                                                        color="success"
                                                        onClick={() => setLifecycleTarget({ definition: activeDefinition, action: "enable" })}
                                                    >
                                                        <PlayCircleOutline />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : (
                                                <Tooltip title={t("fluss.repository.disable", { defaultValue: "Deactivate" })}>
                                                    <IconButton
                                                        color="warning"
                                                        onClick={() => setLifecycleTarget({ definition: activeDefinition, action: "disable" })}
                                                    >
                                                        <Block />
                                                    </IconButton>
                                                </Tooltip>
                                            )
                                        )}
                                        {canDelete && (
                                            <Tooltip title={t("fluss.repository.delete", { defaultValue: "Delete" })}>
                                                <IconButton
                                                    color="error"
                                                    onClick={() => setLifecycleTarget({ definition, action: "delete" })}
                                                >
                                                    <Delete />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <Tooltip title={t("fluss.repository.copy", { defaultValue: "Copy" })}>
                                            <IconButton onClick={() => openCopy(definition)}>
                                                <ContentCopy />
                                            </IconButton>
                                        </Tooltip>
                                        <Button
                                            variant="outlined"
                                            startIcon={<Edit />}
                                            onClick={() => navigate(`/home/fluss/definitions/${definition.id}`)}
                                        >
                                            {t("fluss.common.open", { defaultValue: "Open" })}
                                        </Button>
                                    </Stack>
                                </Stack>
                            </Paper>
                            );
                        })}
                    </Stack>
                )}
            </Stack>

            <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {copySource
                        ? t("fluss.repository.copy_title", { defaultValue: "Copy Fluss definition" })
                        : t("fluss.repository.create_title", { defaultValue: "New Fluss definition" })}
                </DialogTitle>
                <DialogContent>
                    <Stack gap={2} sx={{ pt: 1 }}>
                        {copySource && (
                            <Typography variant="body2" color="text.secondary">
                                {t("fluss.repository.copy_source", {
                                    defaultValue: "Source: {{name}}",
                                    name: flussDefinitionLabel(t, copySource.name),
                                })}
                            </Typography>
                        )}
                        <TextField
                            autoFocus
                            label={t("fluss.definition.name", { defaultValue: "Name" })}
                            value={name}
                            onChange={(event) => {
                                setName(event.target.value);
                                if (!keyTouched) setDefinitionKey(toDefinitionKey(event.target.value));
                            }}
                            fullWidth
                        />
                        <TextField
                            label={t("fluss.definition.key", { defaultValue: "Technical key" })}
                            value={definitionKey}
                            onFocus={(event) => { if (!keyTouched) event.target.select(); }}
                            onChange={(event) => {
                                setKeyTouched(true);
                                const typed = event.target.value;
                                setDefinitionKey(toDefinitionKey(typed) + (typed.endsWith("_") && typed.length > 1 ? "_" : ""));
                            }}
                            fullWidth
                        />
                        <TextField
                            label={t("fluss.definition.description", { defaultValue: "Description" })}
                            value={description}
                            onChange={(event) => setDescription(event.target.value)}
                            multiline
                            minRows={3}
                            fullWidth
                        />
                        {createMutation.error && <Alert severity="error">{flussApiError(createMutation.error).message}</Alert>}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button variant="text" onClick={() => setCreateOpen(false)}>
                        {t("fluss.common.cancel", { defaultValue: "Cancel" })}
                    </Button>
                    <Button
                        disabled={!name.trim() || !(definitionKey || toDefinitionKey(name)) || createMutation.isPending}
                        onClick={() => createMutation.mutate()}
                    >
                        {copySource
                            ? t("fluss.common.copy", { defaultValue: "Copy" })
                            : t("fluss.common.create", { defaultValue: "Create" })}
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmActionDialog
                open={Boolean(lifecycleTarget)}
                title={
                    lifecycleTarget?.action === "delete"
                        ? t("fluss.repository.delete_confirm_title", { defaultValue: "Delete this definition?" })
                        : lifecycleTarget?.action === "enable"
                            ? t("fluss.repository.enable_confirm_title", { defaultValue: "Activate this definition?" })
                            : t("fluss.repository.disable_confirm_title", { defaultValue: "Deactivate this definition?" })
                }
                description={(
                    <Stack gap={1}>
                        <Typography color="text.secondary">
                            {lifecycleTarget?.action === "delete"
                                ? t("fluss.repository.delete_confirm_description", {
                                    defaultValue: "This permanently removes every version of \"{{name}}\" and its published snapshots. Only allowed when the definition has no instances.",
                                    name: flussDefinitionLabel(t, lifecycleTarget?.definition.name),
                                })
                                : lifecycleTarget?.action === "enable"
                                    ? t("fluss.repository.enable_confirm_description", {
                                        defaultValue: "\"{{name}}\" will accept new instances again.",
                                        name: flussDefinitionLabel(t, lifecycleTarget?.definition.name),
                                    })
                                    : t("fluss.repository.disable_confirm_description", {
                                        defaultValue: "\"{{name}}\" will stop accepting new instances from every trigger (record, manual and API). Instances already running keep going.",
                                        name: flussDefinitionLabel(t, lifecycleTarget?.definition.name),
                                    })}
                        </Typography>
                        {lifecycleError && <Alert severity="error">{lifecycleError}</Alert>}
                    </Stack>
                )}
                confirmLabel={
                    lifecycleTarget?.action === "delete"
                        ? t("fluss.repository.delete", { defaultValue: "Delete" })
                        : lifecycleTarget?.action === "enable"
                            ? t("fluss.repository.enable", { defaultValue: "Activate" })
                            : t("fluss.repository.disable", { defaultValue: "Deactivate" })
                }
                cancelLabel={t("fluss.common.cancel", { defaultValue: "Cancel" })}
                busy={lifecycleMutation.isPending}
                onConfirm={() => lifecycleMutation.mutate()}
                onClose={() => {
                    setLifecycleTarget(null);
                    setLifecycleError(null);
                }}
            />
        </Box>
    );
};

export default FlussRepositoryPage;
